import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Peer from 'peerjs';
import {
  remoteGetDisplay,
  remoteJoinRoom,
  remotePollMe,
  remotePostCommand,
} from '../utils/remoteApi';

import { formatStopwatchSeconds } from '../utils/stopwatchFormat';
import { normalizeRemoteRoomCode } from '../utils/remoteJoinPath';
import '../styles/RemotePresenter.css';

function readRoomFromUrl() {
  try {
    return new URLSearchParams(window.location.search).get('room') || '';
  } catch {
    return '';
  }
}

function detectDeviceName() {
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';

  // Android: try to extract model, fall back to generic label
  if (/Android/.test(ua)) {
    const model = ua.match(/;\s*([^;)]+)\s+Build\//);
    if (model) return model[1].trim();
    return 'Android';
  }

  // iOS devices
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'iPad';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPod/.test(ua)) return 'iPod';

  // Chrome OS
  if (/CrOS/.test(ua)) return 'Chromebook';

  // macOS
  if (/Macintosh|Mac OS X/.test(ua)) return 'Mac';

  // Windows
  if (/Windows/.test(ua)) return 'Windows PC';

  // Linux (after Android check, so real Linux only)
  if (/Linux/.test(ua)) return 'Linux';

  return platform || 'Unknown device';
}

const SESSION_KEY = 'deckpack-remote-session';
function loadSavedSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (s?.roomId && s?.clientId && s?.remoteToken && s?.name) return s;
  } catch { /* ignore */ }
  return null;
}
function saveSession(s) {
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}
function clearSession() {
  try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
}

/**
 * One-shot bootstrap: URL room (path or ?room=) wins over a stale saved session for a different code.
 * Called once per mount (parent should `key` remotes when the join code changes).
 */
function computeRemoteInitialState(pathRoomId) {
  const fromQuery = readRoomFromUrl();
  const pathNorm = normalizeRemoteRoomCode(typeof pathRoomId === 'string' ? pathRoomId : '');
  const queryNorm = normalizeRemoteRoomCode(fromQuery);
  const urlRoom = pathNorm || queryNorm;

  let saved = loadSavedSession();
  if (urlRoom && saved?.roomId && normalizeRemoteRoomCode(saved.roomId) !== urlRoom) {
    clearSession();
    saved = null;
  }

  const savedRoom = saved?.roomId ? normalizeRemoteRoomCode(saved.roomId) : '';

  if (saved?.remoteToken && saved?.clientId && saved?.name && savedRoom && (!urlRoom || savedRoom === urlRoom)) {
    return {
      step: 'live',
      roomId: savedRoom,
      name: saved.name,
      clientId: saved.clientId,
      remoteToken: saved.remoteToken,
    };
  }

  if (urlRoom) {
    return {
      step: 'name',
      roomId: urlRoom,
      name: saved && savedRoom === urlRoom && saved.name ? saved.name : '',
      clientId: '',
      remoteToken: '',
    };
  }

  return {
    step: 'room',
    roomId: '',
    name: '',
    clientId: '',
    remoteToken: '',
  };
}

/** Same semantic fields as App.js `getRemoteSnapshotRef` (presenter truth). */
function sameRemoteSnapshot(a, b) {
  if (!a || !b) return false;
  return (
    a.presentationId === b.presentationId &&
    a.slideIndex === b.slideIndex &&
    a.slideCount === b.slideCount &&
    a.timerSec === b.timerSec &&
    a.timerRunning === b.timerRunning &&
    a.timerLabel === b.timerLabel &&
    a.overlay === b.overlay &&
    a.autoAdvance === b.autoAdvance &&
    a.deckName === b.deckName &&
    a.currentThumb === b.currentThumb &&
    a.prevThumb === b.prevThumb &&
    a.nextThumb === b.nextThumb &&
    a.notesHtml === b.notesHtml
  );
}

/**
 * P2P snapshots omit `updatedAt` (HTTP mirror adds it in the DO). Normalize so we can still order
 * vs stale HTTP responses.
 */
function normalizeRemoteSnapshot(prev, d) {
  if (typeof d.updatedAt === 'number') return d;
  const base = typeof prev?.updatedAt === 'number' ? prev.updatedAt : 0;
  return { ...d, updatedAt: Math.max(Date.now(), base + 1) };
}

/**
 * Apply presenter display snapshot only — no optimistic local slide. Matches console / DO truth.
 */
function applyRemoteDisplaySnapshot(prev, d) {
  if (!d) return prev;
  if (!prev) return normalizeRemoteSnapshot(null, d);

  if (d.presentationId != null && prev.presentationId != null && d.presentationId !== prev.presentationId) {
    return normalizeRemoteSnapshot(prev, d);
  }

  const incoming = normalizeRemoteSnapshot(prev, d);

  if (sameRemoteSnapshot(prev, incoming)) {
    return prev;
  }

  if (
    typeof prev.updatedAt === 'number' &&
    typeof incoming.updatedAt === 'number' &&
    incoming.updatedAt < prev.updatedAt
  ) {
    return prev;
  }

  return incoming;
}

export default function RemotePresenterClient({ pathRoomId }) {
  const liveSurfaceRef = useRef(null);

  useEffect(() => {
    document.title = 'Deck Pack Remote';
  }, []);

  const initialBootRef = useRef(null);
  if (initialBootRef.current === null) {
    initialBootRef.current = computeRemoteInitialState(pathRoomId);
  }
  const boot = initialBootRef.current;
  const [step, setStep] = useState(() => boot.step);
  const [roomId, setRoomId] = useState(() => boot.roomId);
  const [name, setName] = useState(() => boot.name);
  const [clientId, setClientId] = useState(() => boot.clientId);
  const [remoteToken, setRemoteToken] = useState(() => boot.remoteToken);
  const [statusNote, setStatusNote] = useState('');
  const [display, setDisplay] = useState(null);
  const [error, setError] = useState('');
  const [peerConnected, setPeerConnected] = useState(false);
  const [reconnectKey, setReconnectKey] = useState(0);

  /* Live session: lock document scroll so only `.remote-notes-content` scrolls. */
  useEffect(() => {
    if (step !== 'live') return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [step]);

  /** Installable PWA scoped to /remote (manifest + minimal service worker). */
  useEffect(() => {
    const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    const manifestLink = document.querySelector('link[rel="manifest"]');
    const prevManifest = manifestLink?.getAttribute('href') ?? null;
    if (manifestLink) {
      manifestLink.setAttribute('href', `${base}/remote-manifest.json`);
    }
    let swTimer = null;
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      // Register after first paint so join + first slide preview are not competing with SW install.
      swTimer = window.setTimeout(() => {
        navigator.serviceWorker.register(`${base}/remote/remote-sw.js`, { scope: '/remote/' }).catch(() => {
          /* install still works in many browsers without SW; ignore */
        });
      }, 2500);
    }
    return () => {
      if (swTimer != null) clearTimeout(swTimer);
      if (manifestLink && prevManifest != null) manifestLink.setAttribute('href', prevManifest);
    };
  }, []);

  const peerRef = useRef(null);
  const connRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  /** Only rebuild PeerJS after a real tab hide → show, not spurious `visibilitychange` (e.g. exiting fullscreen). */
  const tabWasHiddenRef = useRef(typeof document !== 'undefined' && document.visibilityState === 'hidden');
  /** Prevents overlapping next/prev POSTs (double taps / key autorepeat) from advancing two slides. */
  const navPostInFlightRef = useRef(false);
  /**
   * Monotonic generation for `remoteGetDisplay`: any newer fetch OR a P2P `state` message bumps this
   * so an older in-flight HTTP response cannot overwrite fresher UI (stops flash/skip).
   */
  const displayHttpSeqRef = useRef(0);
  /** Latest overlay from `display` for click handlers (avoids stale closures vs `send`). */
  const remoteOverlayRef = useRef('none');

  const effectiveRoom = roomId.trim().toUpperCase();

  const mergeHttpDisplaySnapshot = useCallback(() => {
    const mySeq = ++displayHttpSeqRef.current;
    void remoteGetDisplay(effectiveRoom, remoteToken)
      .then((data) => {
        if (mySeq !== displayHttpSeqRef.current) return;
        if (data?.display) {
          setDisplay((prev) => applyRemoteDisplaySnapshot(prev, data.display));
        }
      })
      .catch(() => {});
  }, [effectiveRoom, remoteToken]);

  const continueJoin = async () => {
    const r = effectiveRoom;
    if (!r || r.length < 4) {
      setError('Enter a valid join code.');
      return;
    }
    const nm = name.trim();
    if (!nm) {
      setError('Enter your name.');
      return;
    }
    setError('');
    try {
      const res = await remoteJoinRoom(r, nm, detectDeviceName());
      setClientId(res.clientId);
      setStep('wait');
    } catch (e) {
      setError(e?.message || 'Could not join');
    }
  };

  useEffect(() => {
    if (step !== 'wait' || !effectiveRoom || !clientId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const me = await remotePollMe(effectiveRoom, clientId);
        if (cancelled) return;
        if (me.status === 'blocked') {
          clearSession();
          setStatusNote('Access blocked by console.');
          setStep('blocked');
          return;
        }
        if (me.status === 'denied') {
          clearSession();
          setStatusNote('The console denied this connection.');
          setStep('denied');
          return;
        }
        if (me.status === 'approved' && me.remoteToken) {
          setRemoteToken(me.remoteToken);
          saveSession({ roomId: effectiveRoom, clientId, name, remoteToken: me.remoteToken });
          setStep('live');
        }
      } catch {
        /* ignore */
      }
    };
    tick();
    const t = window.setInterval(tick, 800);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [step, effectiveRoom, clientId, name]);

  // PeerJS: connect during wait (for fast approval) and live (for state)
  useEffect(() => {
    if ((step !== 'wait' && step !== 'live') || !effectiveRoom || !clientId) return undefined;
    if (step === 'live' && !remoteToken) return undefined;

    const operatorPeerId = `dp-${effectiveRoom}`;
    const iceServers = [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ];
    let destroyed = false;
    const peer = new Peer({ config: { iceServers } });
    peerRef.current = peer;

    peer.on('open', (myId) => {
      if (destroyed) return;
      console.log('[PeerJS:remote] registered as', myId, '→ connecting to', operatorPeerId);
      const conn = peer.connect(operatorPeerId, {
        reliable: true,
        metadata: { clientId, name },
      });
      connRef.current = conn;

      conn.on('open', () => {
        console.log('[PeerJS:remote] DataConnection OPEN');
        if (step === 'live') setPeerConnected(true);
      });

      conn.on('data', (raw) => {
        try {
          const msg = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (msg.type === 'approved' && msg.remoteToken) {
            setRemoteToken(msg.remoteToken);
            saveSession({ roomId: effectiveRoom, clientId, name, remoteToken: msg.remoteToken });
            setStep('live');
            setPeerConnected(true);
            return;
          }
          if (msg.type === 'session_ended') {
            clearSession();
            setDisplay(null);
            setPeerConnected(false);
            setStep('ended');
            if (document.fullscreenElement) document.exitFullscreen?.();
            return;
          }
          if (msg.type === 'state' && msg.data) {
            displayHttpSeqRef.current += 1;
            setDisplay((prev) => applyRemoteDisplaySnapshot(prev, msg.data));
          }
        } catch { /* ignore */ }
      });

      conn.on('close', () => {
        if (destroyed) return;
        console.log('[PeerJS:remote] connection closed — will auto-reconnect');
        setPeerConnected(false);
        reconnectTimerRef.current = setTimeout(() => setReconnectKey((k) => k + 1), 2000);
      });

      conn.on('error', (err) => {
        if (destroyed) return;
        console.warn('[PeerJS:remote] connection error', err);
        setPeerConnected(false);
        reconnectTimerRef.current = setTimeout(() => setReconnectKey((k) => k + 1), 2000);
      });
    });

    peer.on('error', (err) => {
      console.warn('[PeerJS:remote] peer error', err.type, err.message);
      if (err.type === 'peer-unavailable' && step === 'live') {
        clearSession();
        setDisplay(null);
        setStep('ended');
      }
    });

    return () => {
      destroyed = true;
      clearTimeout(reconnectTimerRef.current);
      if (connRef.current) {
        try { connRef.current.close(); } catch { /* ignore */ }
        connRef.current = null;
      }
      if (peerRef.current) {
        try { peerRef.current.destroy(); } catch { /* ignore */ }
        peerRef.current = null;
      }
      setPeerConnected(false);
    };
  }, [step, effectiveRoom, remoteToken, clientId, name, reconnectKey]);

  // HTTP mirror while live: P2P delivers most updates; polling every 100ms hammered the DO and UI.
  // Faster when relay-only; slower when direct P2P is up (HTTP is backup + timer drift correction).
  useEffect(() => {
    if (step !== 'live' || !effectiveRoom || !remoteToken) return undefined;
    let cancelled = false;
    const pollMs = peerConnected ? 2200 : 450;
    const poll = () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      /* Avoid overlapping display GETs with next/prev POST + pull (same seq race as flicker). */
      if (navPostInFlightRef.current) return;
      mergeHttpDisplaySnapshot();
    };
    poll();
    const int = window.setInterval(poll, pollMs);
    const onVis = () => {
      if (document.visibilityState === 'visible') poll();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(int);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [step, effectiveRoom, remoteToken, peerConnected, mergeHttpDisplaySnapshot]);

  // After sleep/background, rebuild PeerJS so direct mode can recover (display still works via HTTP).
  useEffect(() => {
    if (step !== 'live') return undefined;
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        tabWasHiddenRef.current = true;
        return;
      }
      if (document.visibilityState === 'visible' && tabWasHiddenRef.current) {
        tabWasHiddenRef.current = false;
        setReconnectKey((k) => k + 1);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [step]);

  // Commands always go through HTTP. A zombie PeerJS connection can stay `open` while the
  // channel no longer delivers; using P2P first made every button a no-op in that state.
  const send = useCallback(
    (action) => {
      if (!effectiveRoom || !remoteToken) return;

      const pull = () => {
        void mergeHttpDisplaySnapshot();
      };

      if (action === 'next' || action === 'prev') {
        if (navPostInFlightRef.current) return;
        navPostInFlightRef.current = true;

        void remotePostCommand(effectiveRoom, remoteToken, action)
          .catch((e) => {
            setError(e?.message || 'Command failed');
          })
          .finally(() => {
            navPostInFlightRef.current = false;
            pull();
          });
        return;
      }

      void remotePostCommand(effectiveRoom, remoteToken, action)
        .catch((e) => {
          setError(e?.message || 'Command failed');
        })
        .finally(pull);
    },
    [effectiveRoom, remoteToken, mergeHttpDisplaySnapshot]
  );

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [netOnline, setNetOnline] = useState(
    () => (typeof navigator !== 'undefined' ? navigator.onLine : true)
  );
  const [clockStr, setClockStr] = useState(() =>
    new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  );

  useEffect(() => {
    const syncFs = () => {
      const fs = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement
      );
      setIsFullscreen(fs);
    };
    document.addEventListener('fullscreenchange', syncFs);
    document.addEventListener('webkitfullscreenchange', syncFs);
    return () => {
      document.removeEventListener('fullscreenchange', syncFs);
      document.removeEventListener('webkitfullscreenchange', syncFs);
    };
  }, []);

  const stepRef = useRef(step);
  stepRef.current = step;
  const isFullscreenRef = useRef(isFullscreen);
  isFullscreenRef.current = isFullscreen;

  /** Screen Wake Lock API — keeps display on while presenting fullscreen (Chrome/Android; iOS 16.4+ PWA in many cases). */
  useEffect(() => {
    if (step !== 'live' || !isFullscreen) return undefined;
    if (typeof navigator === 'undefined' || !navigator.wakeLock?.request) return undefined;

    let cancelled = false;
    let lock = null;

    const release = () => {
      if (lock) {
        lock.release().catch(() => {});
        lock = null;
      }
    };

    const acquire = async () => {
      if (cancelled || stepRef.current !== 'live' || !isFullscreenRef.current) return;
      try {
        release();
        lock = await navigator.wakeLock.request('screen');
      } catch {
        /* Unsupported, denied, or not allowed in this context */
      }
    };

    void acquire();

    const onVis = () => {
      if (document.visibilityState === 'visible') void acquire();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
      release();
    };
  }, [step, isFullscreen]);

  useEffect(() => {
    const onUp = () => setNetOnline(true);
    const onDown = () => setNetOnline(false);
    window.addEventListener('online', onUp);
    window.addEventListener('offline', onDown);
    return () => {
      window.removeEventListener('online', onUp);
      window.removeEventListener('offline', onDown);
    };
  }, []);

  useEffect(() => {
    const tick = () =>
      setClockStr(new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }));
    const id = window.setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // After entering live, move focus off cross-origin iframe / stray targets so Space reliably hits our handler.
  useEffect(() => {
    if (step !== 'live') return undefined;
    const id = window.requestAnimationFrame(() => {
      try {
        liveSurfaceRef.current?.focus({ preventScroll: true });
      } catch {
        /* ignore */
      }
    });
    return () => cancelAnimationFrame(id);
  }, [step]);

  // Space / Page Down / arrows — remote `next` / `prev` (don’t treat toolbar buttons as “typing” or keys stay dead).
  useEffect(() => {
    if (step !== 'live' || !effectiveRoom || !remoteToken) return undefined;
    const shouldDeferRemoteKeys = (el) =>
      el &&
      typeof el.closest === 'function' &&
      el.closest('input, textarea, select, [contenteditable="true"]');

    const onKeyDown = (e) => {
      if (e.repeat) return;
      if (shouldDeferRemoteKeys(e.target)) return;
      const forward =
        e.code === 'Space' ||
        e.key === ' ' ||
        e.code === 'PageDown' ||
        e.code === 'ArrowRight' ||
        e.code === 'ArrowDown' ||
        e.key === 'ArrowRight' ||
        e.key === 'ArrowDown';
      if (forward) {
        e.preventDefault();
        /* Stop bubbling so a focused “Next” button does not also activate (double-advance). */
        e.stopPropagation();
        send('next');
        return;
      }
      if (
        e.code === 'ArrowLeft' ||
        e.code === 'ArrowUp' ||
        e.code === 'PageUp' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowUp' ||
        e.key === 'PageUp'
      ) {
        e.preventDefault();
        e.stopPropagation();
        send('prev');
      }
    };

    /* document + capture: matches PresenterView; still won’t see keys typed into cross-origin iframe. */
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [step, effectiveRoom, remoteToken, send]);

  const toggleFullscreen = useCallback(() => {
    const el = document.documentElement;
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) void document.exitFullscreen();
      else if (document.webkitExitFullscreen) void document.webkitExitFullscreen();
    } else if (el.requestFullscreen) {
      void el.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      void el.webkitRequestFullscreen();
    }
  }, []);

  const presentationId = display?.presentationId;
  const slideIndex = typeof display?.slideIndex === 'number' ? display.slideIndex : 0;
  const slideNum = slideIndex + 1;

  const remoteSlidesEmbedSrc = useMemo(() => {
    if (!presentationId) return '';
    /* `/embed` + `rm=minimal` tends to show less chrome/filmstrip than `/preview` on narrow phones. */
    return `https://docs.google.com/presentation/d/${presentationId}/embed?rm=minimal&start=false&loop=false&delayms=0&slide=${slideNum}`;
  }, [presentationId, slideNum]);
  const slideCount = display?.slideCount || 0;
  const atFirstSlide = slideIndex <= 0;
  const atLastSlide = slideCount > 0 && slideIndex >= slideCount - 1;
  const timerLabel =
    (typeof display?.timerLabel === 'string' && display.timerLabel) ||
    formatStopwatchSeconds(display?.timerSec ?? 0);
  const timerRunning = Boolean(display?.timerRunning);
  const overlay = display?.overlay === 'black' || display?.overlay === 'white' ? display.overlay : 'none';
  remoteOverlayRef.current = overlay;
  const currentThumb =
    typeof display?.currentThumb === 'string' && display.currentThumb.trim() ? display.currentThumb.trim() : null;

  const cloudStatus = !netOnline ? 'offline' : peerConnected ? 'direct' : 'relay';
  const cloudTitle =
    cloudStatus === 'offline'
      ? 'No network connection'
      : cloudStatus === 'direct'
        ? 'Direct link to presenter'
        : 'Connected via cloud';

  if (step === 'room' || step === 'name' || step === 'wait' || step === 'denied' || step === 'blocked' || step === 'ended') {
    return (
      <div className="remote-client remote-client--gate">
        <div className="remote-gate-card">
          <div className="remote-gate-logo">
            <i className="fa-solid fa-mobile-screen-button" aria-hidden="true" />
          </div>
          <h1 className="remote-gate-title">Deck Pack Remote</h1>
          {step === 'room' ? (
            <>
              <p className="remote-gate-hint">Enter the join code shown on the console.</p>
              <input
                className="remote-input"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                value={roomId}
                onChange={(e) => setRoomId(e.target.value.toUpperCase())}
                placeholder="e.g. ABC123"
                onKeyDown={(e) => e.key === 'Enter' && roomId.trim().length >= 4 && setStep('name')}
              />
              <button
                type="button"
                className="remote-primary-wide"
                disabled={roomId.trim().length < 4}
                onClick={() => setStep('name')}
              >
                Continue
              </button>
            </>
          ) : null}
          {step === 'name' ? (
            <>
              <p className="remote-gate-hint">Your name (shown to the presenter).</p>
              <input
                className="remote-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                maxLength={64}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && name.trim() && continueJoin()}
              />
              <button
                type="button"
                className="remote-primary-wide"
                disabled={!name.trim()}
                onClick={continueJoin}
              >
                Request access
              </button>
              <button type="button" className="remote-link-btn" onClick={() => setStep('room')}>
                <i className="fa-solid fa-arrow-left" aria-hidden="true" /> Back
              </button>
            </>
          ) : null}
          {step === 'wait' ? (
            <div className="remote-wait">
              <div className="remote-spinner" aria-hidden="true" />
              <p className="remote-gate-hint">Waiting for the presenter to approve&hellip;</p>
              <p className="remote-room-pill"><span className="remote-room-pill-label">Code</span> {effectiveRoom}</p>
            </div>
          ) : null}
          {(step === 'denied' || step === 'blocked') && (
            <>
              <div className="remote-gate-denied-icon">
                <i className="fa-solid fa-circle-xmark" aria-hidden="true" />
              </div>
              <p className="remote-gate-error">{statusNote}</p>
              <button type="button" className="remote-link-btn" onClick={() => { clearSession(); setStep('room'); }}>
                Try again
              </button>
            </>
          )}
          {step === 'ended' && (
            <>
              <div className="remote-gate-ended-icon">
                <i className="fa-solid fa-plug-circle-xmark" aria-hidden="true" />
              </div>
              <p className="remote-gate-hint">The presenter ended the remote session.</p>
              <button type="button" className="remote-primary-wide" onClick={() => { clearSession(); setDisplay(null); setStep('room'); }}>
                Reconnect
              </button>
            </>
          )}
          {error ? <p className="remote-gate-error">{error}</p> : null}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={liveSurfaceRef}
      tabIndex={-1}
      className={`remote-client remote-client--live${isFullscreen ? ' remote-client--fullscreen' : ''}`}
    >
      <header className="remote-top-bar">
        <div className="remote-top-meta">
          <span className="remote-slide-pill">
            <span className="remote-slide-num">{slideNum}</span>
            {slideCount ? <span className="remote-slide-of"> / {slideCount}</span> : ''}
          </span>
          {display?.deckName ? <span className="remote-deck-name">{display.deckName}</span> : null}
        </div>
        <div className="remote-toolbar">
          <button type="button" className="remote-toolbar-btn" onClick={() => send('timer_reset')} title="Reset timer">
            <i className="fa-regular fa-clock" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`remote-toolbar-btn${overlay === 'black' ? ' remote-toolbar-btn--active' : ''}`}
            onClick={() => send(remoteOverlayRef.current === 'black' ? 'overlay_none' : 'blackout')}
            title={overlay === 'black' ? 'Clear blackout' : 'Blackout'}
          >
            <i className="fa-regular fa-square" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`remote-toolbar-btn${overlay === 'white' ? ' remote-toolbar-btn--active' : ''}`}
            onClick={() => send(remoteOverlayRef.current === 'white' ? 'overlay_none' : 'whiteout')}
            title={overlay === 'white' ? 'Clear whiteout' : 'Whiteout'}
          >
            <i className="fa-solid fa-square" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="remote-toolbar-btn"
            onClick={toggleFullscreen}
            title={
              isFullscreen
                ? 'Exit fullscreen'
                : 'Fullscreen — also requests screen wake lock where the browser supports it'
            }
          >
            <i className={`fa-solid ${isFullscreen ? 'fa-compress' : 'fa-expand'}`} aria-hidden="true" />
          </button>
        </div>
        <div className="remote-top-right">
          <div className="remote-top-timer" title="Presenter stopwatch">
            <span className="remote-top-timer-label">Timer</span>
            <span
              className={`remote-top-timer-value${timerRunning ? ' remote-top-timer-value--run' : ' remote-top-timer-value--paused'}`}
            >
              {timerLabel}
            </span>
            <span className="remote-top-clock">{clockStr}</span>
          </div>
          <span
            className={`remote-conn-cloud remote-conn-cloud--${cloudStatus}`}
            title={cloudTitle}
            role="img"
            aria-label={cloudTitle}
          >
            <i className={`fa-solid ${cloudStatus === 'offline' ? 'fa-cloud-slash' : 'fa-cloud'}`} aria-hidden="true" />
          </span>
        </div>
      </header>

      <div className="remote-live-body">
        <div className="remote-live-upper">
        <div className="remote-main-grid">
        <section className="remote-stage">
          <div className="remote-slide-frame">
            {currentThumb ? (
              <img
                src={currentThumb}
                alt=""
                className="remote-slide-image"
                decoding="async"
                loading="eager"
                referrerPolicy="no-referrer"
              />
            ) : remoteSlidesEmbedSrc ? (
              <div className="remote-slide-iframe-host">
                <iframe
                  key={presentationId}
                  src={remoteSlidesEmbedSrc}
                  title="Remote slide preview"
                  className="remote-slide-iframe"
                  tabIndex={-1}
                  allow="autoplay; encrypted-media; picture-in-picture"
                />
                <div className="slides-deck-shield" aria-hidden="true" />
              </div>
            ) : presentationId ? (
              <div className="remote-slide-placeholder">
                <i className="fa-regular fa-image" aria-hidden="true" />
                <span>Waiting for slide&hellip;</span>
              </div>
            ) : (
              <div className="remote-slide-placeholder">
                <i className="fa-regular fa-image" aria-hidden="true" />
                <span>No slide sync yet</span>
              </div>
            )}
          </div>
        </section>

        <aside className="remote-sidebar">
          <div className="remote-thumb-block">
            <h4>Prev</h4>
            <div className="remote-thumb remote-thumb--169">
              {display?.prevThumb ? (
                <img
                  className="remote-thumb-img"
                  src={display.prevThumb}
                  alt=""
                  decoding="async"
                  loading="eager"
                  referrerPolicy="no-referrer"
                />
              ) : atFirstSlide ? (
                <div className="remote-thumb-fallback remote-thumb-fallback--pill">
                  <span className="deck-order-pill deck-order-pill--in-thumb">Beginning</span>
                </div>
              ) : (
                <div className="remote-thumb-fallback remote-thumb-fallback--empty" aria-hidden="true" />
              )}
            </div>
          </div>
          <div className="remote-thumb-block">
            <h4>Next</h4>
            <div className="remote-thumb remote-thumb--169">
              {display?.nextThumb ? (
                <img
                  className="remote-thumb-img"
                  src={display.nextThumb}
                  alt=""
                  decoding="async"
                  loading="eager"
                  referrerPolicy="no-referrer"
                />
              ) : atLastSlide ? (
                <div className="remote-thumb-fallback remote-thumb-fallback--pill">
                  <span className="deck-order-pill deck-order-pill--in-thumb">End</span>
                </div>
              ) : (
                <div className="remote-thumb-fallback remote-thumb-fallback--empty" aria-hidden="true" />
              )}
            </div>
          </div>
          {overlay !== 'none' && (
            <div className={`remote-overlay-badge remote-overlay-badge--${overlay}`} aria-live="polite">
              <i
                className={overlay === 'black' ? 'fa-regular fa-square' : 'fa-solid fa-square'}
                aria-hidden="true"
              />
              {overlay === 'black' ? 'Blackout' : 'Whiteout'}
            </div>
          )}
        </aside>
        </div>
        </div>

        <section className="remote-notes">
          <h3 className="remote-notes-title">
            <i className="fa-regular fa-note-sticky" aria-hidden="true" /> Speaker notes
          </h3>
          <div
            className="remote-notes-content"
            dangerouslySetInnerHTML={{ __html: display?.notesHtml || '<span class="remote-notes-empty">No notes for this slide</span>' }}
          />
        </section>
      </div>

      {error ? <p className="remote-live-error">{error}</p> : null}

      <nav className="remote-controls" aria-label="Presentation controls">
        <div className="remote-controls-row remote-controls-row--primary">
          <button
            type="button"
            className={`remote-btn remote-btn--prev${atFirstSlide ? ' remote-btn--boundary' : ''}`}
            disabled={atFirstSlide}
            title={atFirstSlide ? 'Beginning of deck' : 'Previous slide'}
            onClick={() => send('prev')}
          >
            {atFirstSlide ? (
              <span className="deck-order-pill deck-order-pill--in-nav">Beginning</span>
            ) : (
              <>
                <i className="fa-solid fa-chevron-left" aria-hidden="true" />
                <span>Back</span>
              </>
            )}
          </button>
          <button
            type="button"
            className={`remote-btn remote-btn--next${display?.autoAdvance ? ' remote-btn--next--auto-advance' : ''}`}
            title={
              display?.autoAdvance
                ? 'Next slide — auto-advance on (at last slide, goes to next deck in playlist)'
                : 'Next slide'
            }
            onClick={() => send('next')}
          >
            {display?.autoAdvance ? (
              <i className="fa-solid fa-forward remote-btn-aa-icon" aria-hidden="true" />
            ) : null}
            <span>Next</span>
            <i className="fa-solid fa-chevron-right" aria-hidden="true" />
          </button>
        </div>
      </nav>
    </div>
  );
}
